import React, { useEffect, useState, useRef } from 'react';
import { Spinner, Button, Input, Modal, ModalHeader, ModalBody, Table } from 'reactstrap';
import axios from 'axios';
import { BANTUAN_KARTU_LIMATAHUN, DESA_BANTUAN_TAHUN, API_TIMEOUT } from '../../utils';
import { numberWithCommas } from '../../utils/func';
import s from './CardBantuan.module.scss';

const CARD_WIDTH = 300;
const GAP = 16;
const AUTO_SPEED = 0.8;

const formatRupiah = (n) =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n || 0);

const toSmallCaps = (t) =>
  (t || '')
    .toString()
    .toLowerCase()
    .replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());

const CardItem = ({ item, idx, onDetail }) => (
  <div className={s.sliderCard} key={`${item.id || idx}-${item.tahun}-${item.bantuan}`}>
    <div className={s.card}>
      <div className={s.cardHeader}>
        <span className={s.cardTahun}>{item.tahun}</span>
        <h6 className={s.cardTitle}>{toSmallCaps(item.bantuan || '-')}</h6>
        <span className={s.cardDinas}>{toSmallCaps(item.dinas || '-')}</span>
      </div>
      <div className={s.cardBody}>
        <div className={s.statRow}>
          <span className={s.statLabel}>Total Penerima</span>
          <span className={s.statValue}>{numberWithCommas(item.totalPenerima || 0)}</span>
        </div>
        <div className={`${s.statRow} ${s.statRowDana}`}>
          <span className={s.statLabel}>Total Dana</span>
          <span className={s.statValue}>{formatRupiah(item.totalDana)}</span>
        </div>
        <Button color="primary" size="sm" className={s.btnDetail} onClick={() => onDetail(item)}>
          Detail
        </Button>
      </div>
    </div>
  </div>
);

const CardBantuan = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrollOffset, setScrollOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedTahun, setSelectedTahun] = useState('');
  const [searchBantuan, setSearchBantuan] = useState('');
  const [detailModal, setDetailModal] = useState({ open: false, item: null, desaData: [], loading: false });
  const dragStart = useRef({ x: 0, offset: 0 });
  const trackRef = useRef(null);

  const years = [...new Set((data || []).map((d) => d.tahun))].sort((a, b) => (b || '').localeCompare(a || ''));
  const filteredData = (data || [])
    .filter((d) => !selectedTahun || d.tahun === selectedTahun)
    .filter((d) => !searchBantuan || (d.bantuan || '').toLowerCase().includes(searchBantuan.toLowerCase()));

  const oneSetWidth = filteredData.length * (CARD_WIDTH + GAP);

  useEffect(() => {
    axios
      .get(BANTUAN_KARTU_LIMATAHUN, { timeout: API_TIMEOUT })
      .then((res) => {
        const raw = res.data?.data || [];
        const arr = Array.isArray(raw) ? raw : [];
        setData(arr);
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, []);

  const handleDetail = (item) => {
    if (!item) return;
    setDetailModal({ open: true, item, desaData: [], loading: true });
    const url = DESA_BANTUAN_TAHUN + item.tahun + '?bantuan=' + encodeURIComponent(item.bantuan || '');
    axios
      .get(url, { timeout: API_TIMEOUT })
      .then((res) => {
        const raw = res.data?.data || [];
        setDetailModal((prev) => ({ ...prev, desaData: Array.isArray(raw) ? raw : [], loading: false }));
      })
      .catch(() => setDetailModal((prev) => ({ ...prev, desaData: [], loading: false })));
  };

  const toggleDetailModal = () => setDetailModal((prev) => ({ ...prev, open: !prev.open }));

  useEffect(() => {
    if (!filteredData.length || isDragging) return;
    let id;
    const tick = () => {
      setScrollOffset((prev) => {
        let next = prev + AUTO_SPEED;
        if (next >= oneSetWidth) next -= oneSetWidth;
        return next;
      });
      id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [filteredData.length, oneSetWidth, isDragging]);

  const getClientX = (e) => (e.touches ? e.touches[0].clientX : e.clientX);

  const handleDragStart = (e) => {
    setIsDragging(true);
    dragStart.current = { x: getClientX(e), offset: scrollOffset };
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    const dx = dragStart.current.x - getClientX(e);
    let next = dragStart.current.offset + dx;
    if (next < 0) next = 0;
    if (next > oneSetWidth) next = oneSetWidth;
    setScrollOffset(next);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    dragStart.current = { x: 0, offset: 0 };
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      if (e.cancelable) e.preventDefault();
      const dx = dragStart.current.x - (e.touches ? e.touches[0].clientX : e.clientX);
      let next = dragStart.current.offset + dx;
      if (next < 0) next = 0;
      if (next > oneSetWidth) next = oneSetWidth;
      setScrollOffset(next);
    };
    const onEnd = () => setIsDragging(false);
    document.addEventListener('mousemove', onMove, { passive: false });
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };
  }, [isDragging, oneSetWidth]);

  if (loading) {
    return (
      <div className={s.loadingWrap}>
        <Spinner color="primary" />
        <p className={s.loadingText}>Memuat data bantuan...</p>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className={s.emptyWrap}>
        <i className="eva eva-inbox-outline" />
        <p>Belum ada data bantuan 5 tahun terakhir</p>
      </div>
    );
  }

  return (
    <div className={s.cardBantuan}>
      <div className={s.filterRow}>
        <h6 className={s.sectionTitle}>Bantuan 5 Tahun Terakhir</h6>
        <div className={s.filterGroup}>
          <Input
            type="select"
            value={selectedTahun}
            onChange={(e) => setSelectedTahun(e.target.value)}
            className={s.selectTahun}
          >
            <option value="">Semua Tahun</option>
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </Input>
          <Input
            type="text"
            placeholder="Cari bantuan..."
            value={searchBantuan}
            onChange={(e) => setSearchBantuan(e.target.value)}
            className={s.searchInput}
          />
        </div>
      </div>
      {!filteredData.length ? (
        <p className={s.noResult}>Tidak ada data untuk filter yang dipilih.</p>
      ) : (
      <div
        className={s.sliderWrap}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div
          ref={trackRef}
          className={s.sliderTrack}
          style={{ transform: `translateX(-${scrollOffset}px)` }}
        >
          {filteredData.map((item, idx) => (
            <CardItem key={`a-${item.id || idx}-${item.tahun}-${item.bantuan}`} item={item} idx={idx} onDetail={handleDetail} />
          ))}
          {filteredData.map((item, idx) => (
            <CardItem key={`b-${item.id || idx}-${item.tahun}-${item.bantuan}`} item={item} idx={idx} onDetail={handleDetail} />
          ))}
        </div>
      </div>
      )}

      <Modal isOpen={detailModal.open} toggle={toggleDetailModal} size="lg" className={s.detailModal}>
        <ModalHeader toggle={toggleDetailModal}>
          Distribusi per Desa — {detailModal.item?.bantuan || ''} ({detailModal.item?.tahun || ''})
        </ModalHeader>
        <ModalBody>
          {detailModal.loading ? (
            <div className={s.modalLoading}>
              <Spinner color="primary" size="sm" />
              <span>Memuat data...</span>
            </div>
          ) : (
            <div className={s.detailTableWrap}>
              <Table size="sm" bordered responsive>
                <thead>
                  <tr>
                    <th className={s.thNo}>No</th>
                    <th>Desa</th>
                    <th className={s.thNum}>Penerima</th>
                    <th className={s.thNum}>Total Dana</th>
                    <th className={s.thNum}>Desil 1+2</th>
                  </tr>
                </thead>
                <tbody>
                  {detailModal.desaData.map((row, idx) => (
                    <tr key={`${row.desa}-${idx}`}>
                      <td className={s.thNo}>{idx + 1}</td>
                      <td>{toSmallCaps(row.desa)}</td>
                      <td className={s.thNum}>{(row.jumlah_penerima ?? 0).toLocaleString('id-ID')}</td>
                      <td className={s.thNum}>{formatRupiah(row.total_uang)}</td>
                      <td className={s.thNum}>{(row.total_desil12 ?? 0).toLocaleString('id-ID')}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {!detailModal.loading && !detailModal.desaData.length && (
                <p className={s.modalEmpty}>Tidak ada data distribusi per desa.</p>
              )}
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default CardBantuan;
